import { isArray } from 'lodash';
import * as XLSX from 'xlsx';
import Decimal from 'decimal.js';
import { dayjs } from 'element-plus';

/**
 * 工具方法
 * @author liling
 * @description 跟业务逻辑工具方法
 */

/**
 * 根据回调函数返回值删除对应的对象字段
 * @param { Object } object 对象
 * @param { Function } shouldDelete 回调函数
 * @param {Boolean} deep 是否深度递归处理
 */
export function deleteFields(
    object: Record<string, any>,
    shouldDelete: (object: Record<string, any>, key: string, value: any) => boolean,
    deep: boolean = true
): void {
    Object.entries(object).forEach(([key, value]) => {
        if (Object.prototype.toString.call(value) === '[object Object]' && deep) {
            deleteFields(value, shouldDelete);
        }
        if (shouldDelete(object, key, value)) {
            delete object[key];
        }
    });
}

/**
 * 创建生成一个自定义表单校验函数
 * @param { RegExp } regExp 正则对象
 * @param { String } message 提示信息
 * @param { Boolean } optional 是否可选，如果为true，则在有值时才去做校验，如果为false，则始终去校验。
 * @returns
 */
export function createValidator(param: RegExp | Function, message: string, optional?: boolean) {
    return function (rule: any, value: any, callback: (error?: Error) => void) {
        if (typeof param === 'function') {
            if (param(value)) {
                callback();
            } else {
                callback(new Error(message));
            }
        } else {
            if (optional) {
                if ((value || value === 0) && !param.test(value)) {
                    callback(new Error(message));
                } else {
                    callback();
                }
            } else {
                if (!param.test(value)) {
                    callback(new Error(message));
                } else {
                    callback();
                }
            }
        }
    };
}

/**
 * 清除对象中为null, undefined, ''的数据
 * @param { Object } obj 需要清除null的对象
 * @param { null | undefined | '' [] } values 需要清除的值
 * @returns
 */
export const stripValues = <T extends object>(obj: T, values: Array<unknown> = [null, undefined, '']): T => {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            // delete if the value matches
            if (values.includes(value)) {
                delete obj[key];
                // if it's an object, recursively remove values
            } else if (typeof value === 'object' && value !== null) {
                stripValues(value as T);
            }
        }
    }
    return obj;
};

/**
 * 清除对象中为null的数据
 * @param { Object } obj 需要清除null的对象
 * @returns
 */
export const stripNullValues = <T extends object>(obj: T): T => stripValues<T>(obj, [null]);

/**
 * 获取css变量的值
 * @param name 变量名
 * @returns css变量的值
 */
export const getCssVar = (name: string) => {
    const root = window.document.documentElement;

    return window.getComputedStyle(root).getPropertyValue(name);
};

/**
 * 十六进制颜色值转RGBA颜色值
 * @param hex 十六进制颜色值
 * @param alpha 透明度
 * @returns RGBA颜色值
 */
export const Hex2Rgba = (hex: string, alpha: number = 1): string => {
    // 移除可能包含在颜色值中的 # 号
    hex = hex.replace('#', '');

    // 提取颜色值中的红、绿、蓝分量
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    //
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Blob文件流下载
 */
export const blobToFileAndDownload = (blob: Blob, fileName: string): void => {
    const link = document.createElement('a');
    // 创建下载的链接
    link.href = window.URL.createObjectURL(blob);
    // 下载的文件名
    link.download = fileName;
    // 添加到文档中
    document.body.appendChild(link);
    // 点击下载
    link.click();
    // 下载完成移除元素
    document.body.removeChild(link);
    // 释放内存
    window.URL.revokeObjectURL(link.href);
};

/**
 * 替换zi
 * @param str 原字符
 * @param searchStr 需替换的字符
 * @param newStr 替换后的字符
 * @returns 替换后的字符
 */
export const replaceStr = (str: string, searchStr: string | RegExp, newStr: string) => {
    if (str.indexOf(newStr) === -1) {
        return str.replace(searchStr, newStr);
    }
    return str;
};

/**
 * 把http换成https
 * @param str
 * @returns
 */
export const http2https = (str: string) => {
    return replaceStr(str, 'http://', 'https://');
};

/**
 * 驼峰命名转换成短横线或下划线命名
 * @param str
 * @param mark
 * @returns {void | string | *}
 */
export const getKebabCase = (str: string, mark: string) => {
    str = str.replace(/[A-Z]/g, function (item) {
        return mark + item.toLowerCase();
    });

    return str.charAt(0) === mark ? str.slice(1) : str;
};

/**
 * 空字符转null
 */
export const emptyStr2null = (obj: Record<string, unknown>) => {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (obj[key] === '' || obj[key] === undefined) {
                obj[key] = null;
            }
        }
    }
    return { ...obj };
};

/**
 * 将运力管理详情的配置转化为字段管理的配置，其他entity需要根据实际情况修改这个函数
 */
export const configToFieldsConfig = (
    fields: Common.IDetailConfig[],
    fieldsValueMap: Record<string, any>,
    entityType: string
) => {
    const fieldsConfig = fields.map((field) => {
        const { name, title, editing, fields } = field;
        const fieldSettingFields = fields.map((item) => {
            const { label, field, customField, disableDisplay } = item;
            return {
                label,
                field: entityType === 'transportation_capacity' ? customField : field,
                editType: 'switch',
                activeText: '必填',
                inactiveText: '可选',
                activeValue: 'required',
                inactiveValue: 'not_required',
                editDisabled:
                    disableDisplay ||
                    !fieldsValueMap[`${entityType === 'transportation_capacity' ? customField : field}`]?.nullable
            };
        });
        return {
            name,
            title,
            data: {},
            editing: !!editing,
            fields: fieldSettingFields
        };
    });

    return fieldsConfig;
};

/**
 *
 * @param url 手动加载第三方文件
 * @param loaded 是否已加载过
 * @param callback 加载完成后的回调
 */
export const loadScript = (url: string, loaded: boolean, callback?: () => void) => {
    if (loaded) {
        typeof callback === 'function' && callback();
    } else {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        document.body.appendChild(script);
        script.onload = () => {
            typeof callback === 'function' && callback();
        };
    }
};

/**
 * @name secondToDHMS
 * @description 秒数转 XX天XX小时XX分钟XX秒
 * @param seconds 秒数
 * @returns XX天XX小时XX分钟XX秒
 */
export const secondToDHMS = (seconds: number, accuracy: 'hour' | 'minute' | 'second' = 'second') => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const remainingSeconds = seconds % 60;

    let result = '';
    if (days > 0) {
        result += `${days}天`;
    }
    if (hours > 0 && ['hour', 'minute', 'second'].includes(accuracy)) {
        result += `${hours}小时`;
    }
    if (minutes > 0 && ['minute', 'second'].includes(accuracy)) {
        result += `${minutes}分钟`;
    }
    if (remainingSeconds > 0 && accuracy === 'second') {
        result += `${remainingSeconds}秒`;
    }

    return result.trim();
};

/**
 * @name lnglatToAddressByBMapGL
 * @description 经纬度解析地址
 * @param point BMapGL.point对象
 * @returns 具体位置信息
 */
export const lnglatToAddressByBMapGL = (point: any) => {
    const geocoder = new BMapGL.Geocoder();
    return new Promise<string>((resolve, reject) => {
        geocoder.getLocation(point, function (result: any) {
            if (result) {
                resolve(result.address);
            } else {
                resolve('');
            }
        });
    });
};

/**
 * @name lnglatToAddressByAMap
 * @description 经纬度解析地址
 * @param pos
 * @returns 具体位置信息
 */
export const lnglatToAddressByAMap = (pos: any) => {
    const geocoder = new AMap.Geocoder();
    return new Promise<{
        formattedAddress: string;
        addressComponent: {
            province: string;
            city: string;
            district: string;
        };
    }>((resolve, reject) => {
        geocoder.getAddress(pos, function (status: any, result: any) {
            if (status === 'complete' && result.regeocode) {
                const {
                    formattedAddress,
                    addressComponent: { province, city, district }
                } = result.regeocode;
                resolve(result.regeocode);
            } else {
                reject('根据经纬度查询地址失败');
            }
        });
    });
};

export const sleep = async (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * getValueByKeyString
 * @description 当key为string时，通过key获取对象对应的值；比如当key传入'a'返回 obj[a], 传入a.b返回obj[a][b]
 * @description 当key为string[]时，对数组中每个k同上处理；返回格式为string[]，使用方自定义处理
 * @param { Object } obj 对象
 * @param { string | string[] } key 键
 */
export const getValueByKeyString = (obj: Record<string, unknown>, key: string | string[]) => {
    if (isArray(key)) {
        const res: unknown[] = [];
        key.forEach((i, idx) => {
            const keys = i.split('.');
            let value = obj;

            for (const k of keys) {
                value = value[k];
                if (typeof value === 'undefined' || value === null) {
                    return undefined;
                }
            }
            res.push(value);
        });
        return res;
    } else {
        const keys = key.split('.');
        let value = obj;

        for (const k of keys) {
            value = value[k];
            if (typeof value === 'undefined' || value === null) {
                return undefined;
            }
        }

        return value;
    }
};

/**
 * 把枚举转换成数组
 * @param enumObj 枚举对象
 * @returns 数组
 */
export const enumToDictArray = (
    enumObj: Record<string, string | number>,
    isNumber: boolean = false
): Common.IDictItem[] => {
    const enumArray: Common.IDictItem[] = [];
    for (const key in enumObj) {
        if (isNumber) {
            if (typeof enumObj[key] === 'number') {
                enumArray.push({
                    label: key,
                    value: enumObj[key]
                });
            }
        } else {
            if (typeof enumObj[key] === 'string') {
                enumArray.push({
                    label: key,
                    value: enumObj[key]
                });
            }
        }
    }

    return enumArray;
};

/**
 * 从数组创建excel表，并下载
 * @param data 要下载的数据
 * example:   
 * data = [
    ["S", "h", "e", "e", "t", "J", "S"],
    [  1,   2,    ,    ,   5,   6,   7],
    [  2,   3,    ,    ,   6,   7,   8],
    [  3,   4,    ,    ,   7,   8,   9],
    [  4,   5,   6,   7,   8,   9,   0]
  ]
 * 
 * @param sheetName 要下载的文件名称
 */
export const downloadArrayofArrays = (data: any[][], sheetName: string) => {
    /* create worksheet */
    const ws = XLSX.utils.aoa_to_sheet(data);
    /* create workbook and export */
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${sheetName}.xlsx`);
};

/**
 * 从对象数组创建excel表，并下载
 * @param data 要下载的数据
 * example:   
 * data = [
    { Name: "Bill Clinton", Index: 42 },
    { Name: "GeorgeW Bush", Index: 43 },
    { Name: "Barack Obama", Index: 44 },
    { Name: "Donald Trump", Index: 45 },
    { Name: "Joseph Biden", Index: 46 }
  ]
 * 
 * @param sheetName 要下载的文件名称
 */
export const downloadArrayofObjects = (data: object[], sheetName: string) => {
    /* create worksheet */
    const ws = XLSX.utils.json_to_sheet(data);
    /* create workbook and export */
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${sheetName}.xlsx`);
};

function getHeaderRow(sheet: any) {
    const headers = []; // 定义数组，用于存放解析好的数据
    const range = XLSX.utils.decode_range(sheet['!ref']); // 读取sheet的单元格数据
    const R = range.s.r;
    for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];
        /* 查找第一行中的单元格 */
        let hdr = 'UNKNOWN ' + C;
        if (cell && cell.t) {
            hdr = XLSX.utils.format_cell(cell);
        }
        headers.push(hdr);
    }
    return headers;
}

interface ParseSheetResult {
    header: string[];
}
/**
 * 解析上传的excel文件
 * @param file 选择的文件
 *
 */
export const parseSheet = async (file: any): Promise<ParseSheetResult> => {
    // parseSheet debug
    /**
     * 1. 使用原生api去读取好的文件
     * */
    // 读取文件不是立马能够读取到的，所以是异步的，使用Promise
    const dataBinary = await new Promise((resolve) => {
        // Web API构造函数FileReader，可实例化对象，去调用其身上方法，去读取解析文件信息
        const reader = new FileReader(); // https://developer.mozilla.org/zh-CN/docs/Web/API/FileReader
        reader.readAsBinaryString(file.raw); // 读取raw的File文件
        reader.onload = (ev) => {
            resolve(ev.target?.result); // 将解析好的结果扔出去，以供使用
        };
    });
    /**
     * 2. 使用xlsx插件去解析已经读取好的二进制excel流文件
     * */
    const workBook = XLSX.read(dataBinary, { type: 'binary', cellDates: true });
    // excel中有很多的sheet，这里取了第一个sheet：workBook.SheetNames[0]
    const firstWorkSheet = workBook.Sheets[workBook.SheetNames[0]];
    // 分为第一行的数据，和第一行下方的数据
    const header: string[] = XLSX.utils.sheet_to_json(firstWorkSheet, { header: 1, range: 'A1:GX1' })[0] as string[];
    return {
        header
    };
};

/**
 * 临时解决js处理浮点数的精度问题
 * @param firstNo 数1
 * @param secondNo 数2
 * @param keepDecimal 保留几位小数
 * @param type 加减乘除
 * @returns
 */
export const getPrecisionNumberOld = (
    firstNo: number | string,
    secondNo: number | string,
    keepDecimal: number,
    type: 'add' | 'subtract' | 'multiply' | 'divide'
) => {
    // 获取数字的小数点位数
    const firstDecimal = firstNo.toString().split('.')[1]?.length || 0;
    const secondDecimal = secondNo.toString().split('.')[1]?.length || 0;

    // 需要转换为整数
    const firstTempNo = Math.pow(10, firstDecimal);
    const secondTempNo = Math.pow(10, secondDecimal);
    // 计算完后恢复小数点位数
    const multiplyTempNo = Math.max(firstTempNo, secondTempNo);

    const firstPureNo = Number(firstNo);
    const secondPureNo = Number(secondNo);

    let res = '';

    if (type === 'add') {
        res = (
            Math.round(
                ((firstPureNo * multiplyTempNo + secondPureNo * multiplyTempNo) / multiplyTempNo) *
                    Math.pow(10, keepDecimal)
            ) / Math.pow(10, keepDecimal)
        ).toString();
    }
    if (type === 'subtract') {
        res = (
            Math.round(
                ((firstPureNo * multiplyTempNo - secondPureNo * multiplyTempNo) / multiplyTempNo) *
                    Math.pow(10, keepDecimal)
            ) / Math.pow(10, keepDecimal)
        ).toString();
    }
    if (type === 'multiply') {
        res = (
            Math.round(
                ((firstPureNo * multiplyTempNo * (secondPureNo * multiplyTempNo)) / (multiplyTempNo * multiplyTempNo)) *
                    Math.pow(10, keepDecimal)
            ) / Math.pow(10, keepDecimal)
        ).toString();
    }
    if (type === 'divide') {
        res = (
            Math.round(((firstPureNo * multiplyTempNo) / (secondPureNo * multiplyTempNo)) * Math.pow(10, keepDecimal)) /
            Math.pow(10, keepDecimal)
        ).toString();
    }

    return Number(res).toFixed(keepDecimal);
};

/**
 *  新版本的计算方式
 * @param firstNo 数1
 * @param secondNo 数2
 * @param keepDecimal 保留几位小数 如传入 -1 则直接返回结算结果
 * @param type 加减乘除
 * @returns
 */
export const getPrecisionNumber = (
    firstNo: number | string,
    secondNo: number | string,
    keepDecimal: number,
    type: 'add' | 'subtract' | 'multiply' | 'divide'
) => {
    const firstDecimal = new Decimal(firstNo ? firstNo : 0);
    const secondDecimal = new Decimal(secondNo ? secondNo : 0);

    let res: Decimal = new Decimal(0);

    if (type === 'add') {
        res = firstDecimal.plus(secondDecimal);
    }
    if (type === 'subtract') {
        res = firstDecimal.minus(secondDecimal);
    }
    if (type === 'multiply') {
        res = firstDecimal.times(secondDecimal);
    }
    if (type === 'divide') {
        res = firstDecimal.div(secondDecimal);
    }

    return keepDecimal >= 0 ? res.toFixed(keepDecimal) : res.toString();
};

/**
 * 根据callback返回结果改变数组顺序
 * @name changeListOrder
 * @param list 要调整的列表
 * @param position first/last要调整到的位置，first: 前面， last: 后面
 * @param callback
 * @returns
 */
export function changeListOrder<T>(list: T[], position: 'first' | 'last', callback: (item: T) => boolean) {
    const list1: T[] = [];
    const list2: T[] = [];

    list.forEach((item) => {
        if (callback(item)) {
            list1.push(item);
        } else {
            list2.push(item);
        }
    });

    return position === 'first' ? list1.concat(list2) : list2.concat(list1);
}

export const getLabelFromDict = (value: string | number, dict: Common.IDictItem[]): string => {
    const item = dict.find((item) => item.value === String(value));

    return item ? item.label : '';
};

/**
 * Json.parse 如果解析失败会报错，如果报错可能阻塞脚本，用这个函数可以防止阻塞，保持代码整洁，不用在每个地方都写try catch
 * @param data 需要解析的字符串.
 * @param fallback 解析失败后返回的字段，默认为`null`.
 * @returns 解析后的json或者解析失败的fallback.
 */
export function safeJsonParse(data: string, fallback: object | null = null) {
    try {
        const parsed = JSON.parse(data);
        return parsed;
    } catch (err) {
        return fallback;
    }
}

/**
 * 过滤选项
 * @name pickSelectOptions
 * @param options
 * @param value
 * @returns
 */
export const pickSelectOptions = (options: Common.IDictItem[], value: (string | number)[]) => {
    return options.filter((item) => value.includes(item.value));
};

/*
 * 递归实现深度克隆
 * @param obj 需要克隆的对象
 * @returns 克隆后的对象
 */
export const deepClone = (obj: any): any => {
    if (obj === null) return null;
    if (typeof obj !== 'object') return obj;

    if (obj instanceof Date) return new Date(obj);
    if (obj instanceof RegExp) return new RegExp(obj);

    const clone: any = Array.isArray(obj) ? [] : {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            clone[key] = deepClone(obj[key]);
        }
    }

    return clone;
};

/**
 * 把对象的值替换为字典中对应的标签
 * @param fields
 * @param dict
 * @param data
 */
export const replaceValueToLabel = (fields: string[], dicts: Common.IDictItem[][], data: any) => {
    const _data = deepClone(data);
    fields.forEach((field, index) => {
        const value = _data[field];
        if (value !== undefined) {
            const label = getLabelFromDict(value, dicts[index]);
            _data[field] = label;
        }
    });
    return _data;
};

/**
 * 金额字段格式转换千分位
 * @param source
 */
export const formatDecimalWithCommas = (source: string | number) => {
    // 1. 统一转为字符串处理
    const numStr = typeof source === 'number' ? source.toString() : source;

    // 2. 校验是否为有效数字格式（支持负数、小数）
    if (!/^-?\d*\.?\d+$/.test(numStr)) return numStr;

    // 3. 分离整数部分和小数部分
    const [integerPart, decimalPart] = numStr.split('.');

    // 4. 处理整数部分千分位（兼容负号）
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 5. 拼接小数部分（如果存在）
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * 数字原样输出（价格/金额展示口径）
 * 整数不带小数位，小数去掉尾部多余的 0：2.0000 → 2、2.5000 → 2.5、12.3456 → 12.3456
 * 空值返回 ''，非数字文本原样返回（避免把日期、编码等误处理）
 * @param value 待处理的值（number / 数字字符串）
 */
export const formatNumberPlain = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : '';
    }
    const text = String(value).trim();
    // 仅处理纯数字（含负号与小数），其它文本原样返回
    if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
    // 无小数点直接返回，避免把 "20" 误裁成 "2"
    if (!text.includes('.')) return text;
    return text.replace(/0+$/, '').replace(/\.$/, '');
};

/**
 * 计算日期，比较出来是今天，明天，昨天还是具体日期
 * @param start  开始时间
 * @returns
 */
export const formatDateTime = (dateTime: any) => {
    const now = dayjs().startOf('day'); // 今天零点
    const target = dayjs(dateTime).startOf('day'); // 目标日期零点
    const diffInDays = now.diff(target, 'day');

    // 今天: 直接显示时间（如 "14:30"）
    if (diffInDays === 0) {
        return dayjs(dateTime).format('HH:mm');
    }
    // 7天内: 显示 "X天前"
    else if (diffInDays <= 7) {
        return `${diffInDays}天前 ${dayjs(dateTime).format('HH:mm')}`;
    }
    // 7天外: 显示完整日期时间（如 "2025/04/28 00:19"）
    else {
        return dayjs(dateTime).format('YYYY/MM/DD HH:mm');
    }
};

/**
 * 计算勾选 选中的合计值
 * @param allColumns  当前表格的 Columns
 * @param selectValue  当前表格的 选择的值
 * @returns
 */
export const calculateSelectedTotal = (allColumns: Common.ITableCol[], selectValue: any[]) => {
    const allNumSumColumns: any = {};

    /** 获取 是 数值类型的 Columns */
    const numColumns = allColumns.filter((i) => i?.type === 'number')?.map((i) => i?.prop);

    numColumns.forEach((column: string) => {
        /** 过滤出来 有值的数组 */
        const hasNewNUm = selectValue.map((newColumn: any) => newColumn[column]).filter((i) => i);

        /** 赋值给新对象  新的合计值参数 */
        const newColumnNum = column + '_sum';

        if (hasNewNUm?.length) {
            /** 取对应的小数位数 */
            const decimalIndex = hasNewNUm[0]?.split('.')?.[1]?.length;

            /** 求所有的数值总和 */
            let newSum = '';
            hasNewNUm?.map((i) => {
                /** 计算求和的值 */
                newSum = getPrecisionNumber(i || '', newSum || '', -1, 'add');

                /** 保留对应的 小数位数 */
                newSum = getPrecisionNumber('', newSum || '', decimalIndex, 'add');
            });
            allNumSumColumns[newColumnNum] = newSum;
        } else {
            allNumSumColumns[newColumnNum] = '';
        }
    });

    return allNumSumColumns;
};

export default {
    deleteFields,
    createValidator,
    stripValues,
    stripNullValues,
    getCssVar,
    Hex2Rgba,
    blobToFileAndDownload,
    replaceStr,
    getKebabCase,
    emptyStr2null,
    loadScript,
    secondToDHMS,
    lnglatToAddressByBMapGL,
    sleep,
    getValueByKeyString,
    enumToDictArray,
    downloadArrayofArrays,
    downloadArrayofObjects,
    parseSheet,
    getPrecisionNumber,
    changeListOrder,
    getLabelFromDict,
    safeJsonParse,
    pickSelectOptions,
    replaceValueToLabel,
    deepClone,
    formatDecimalWithCommas,
    formatNumberPlain,
    formatDateTime,
    calculateSelectedTotal
};
